import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { QueryParamConfig } from 'use-query-params';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { ProjectEntitiesQueryContext, ProjectEntitiesQueryParamsContext } from './Context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type Props = {
  children: React.ReactNode;
  enabled: boolean;
} & IProjectId;

export const ProjectEntitiesQueryContainer = (props: Props) => {
  const [qp, setQuery] = useQueryParams({
    index: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 75),
    sortBy: withDefault(StringParam, 'total') as QueryParamConfig<'boosted' | 'name' | 'total' | 'type'>,
    sortDir: withDefault(StringParam, 'desc') as QueryParamConfig<'asc' | 'desc'>,
    nameFilter: StringParam,
  });

  const qk = useMemo(() => {
    return [
      `get:projects/entities`, {
        index: qp.index,
        size: qp.size,
        projectId: props.projectId,
        sortBy: qp.sortBy,
        sortDir: qp.sortDir,
        nameFilter: qp.nameFilter,
      },
    ];
  }, [
    props.projectId,
    qp.index,
    qp.size,
    qp.sortBy,
    qp.sortDir,
    qp.nameFilter,
  ]);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: qk,
    queryFn: () => {
      return apiClient.projectsEntities.fetchItems({
        index: qp.index,
        projectId: props.projectId,
        size: qp.size,
        sortBy: qp.sortBy,
        sortDir: qp.sortDir,
        nameFilter: qp.nameFilter,
      });
    },
    enabled: props.enabled,
    placeholderData: keepPreviousData,
  });

  return (
    <ProjectEntitiesQueryParamsContext.Provider value={[qp, setQuery]}>
      <ProjectEntitiesQueryContext.Provider value={query}>
        {props.children}
      </ProjectEntitiesQueryContext.Provider>
    </ProjectEntitiesQueryParamsContext.Provider>
  );
};

ProjectEntitiesQueryContainer.displayName = 'Project.Entities.Query.Container';